/**
 * FY — Canonical helper for Indian Financial Year handling.
 *
 * India FY = 1-Apr → 31-Mar.  For AUDIT tooling the "current" FY is the
 * most recently concluded one (you audit a year after it ends), not the
 * calendar-current one.
 *
 * ─────────────────────────────────────────────────────────────────────
 * Production lock-down (2026-02-09): per audit-team request, dropdowns
 * surface ONLY the two FYs in active use:
 *   • 2025-26  (current default — last year that just closed 31-Mar-2026)
 *   • 2024-25  (prior year — still being worked on for some clients)
 *
 * When you need to add FY 2026-27 (typically just after 1-Apr-2027),
 * prepend it to ``FY_OPTIONS`` below and update ``DEFAULT_FY``.
 * Anything outside this allow-list will be rejected by ``isValidFy``
 * for *any* user-input flow (URL ``?fy=…``, prompt boxes, etc.).
 * ─────────────────────────────────────────────────────────────────────
 *
 * All FY dropdowns in the app MUST source their list + default from this
 * module.  Do not hard-code FY strings elsewhere.
 */

/** Allow-list of FY options surfaced everywhere (newest first). */
export const FY_OPTIONS = ["2025-26", "2024-25"];

/** Default FY for all new selectors. */
export const DEFAULT_FY = "2025-26";

/**
 * Strict validator — accepts only FYs in the allow-list above.
 *
 * Historically this validated any well-formed "YYYY-YY" string; we now
 * also restrict by allow-list so a stale URL like ``?fy=2018-19`` from
 * a bookmarked page doesn't sneak past.
 */
export function isValidFy(s) {
  return FY_OPTIONS.includes(String(s || "").trim());
}

/** "2025-26" → { start: "2025-04-01", end: "2026-03-31", startYear: 2025, endYear: 2026 }. */
export function parseFy(s) {
  if (!isValidFy(s)) return null;
  const [startStr] = s.split("-");
  const startYear = parseInt(startStr, 10);
  const endYear = startYear + 1;
  return {
    start: `${startYear}-04-01`,
    end:   `${endYear}-03-31`,
    startYear,
    endYear,
    label: s,
  };
}

/** Kept for back-compat; returns the static allow-list. */
export function fyOptions() {
  return FY_OPTIONS;
}

/** Kept for back-compat; returns ``DEFAULT_FY``. */
export function currentAuditFy() {
  return DEFAULT_FY;
}
